import type { ComputedRef } from 'vue';
import type OlMapWrapper from 'src/lib/OlMapWrapper';
import type { FitOptions } from 'ol/View';
import type { Coordinate } from 'ol/coordinate';
import type { Location } from 'src/models';
import type { GIMovieGeoIndex, GIMovieList } from 'src/models/geoItem';
import type { Settings as UserSettings } from 'src/models/user';

interface Deps {
  olMap: OlMapWrapper;
  olMapId: ComputedRef<string>;
  // userSettingsは初期化完了後にstateへ格納されるため、呼び出し時点の値をgetterで参照する
  getUserSettings: () => UserSettings;
}

interface ExtremeMapViewControlContext {
  applyInitialExtent: () => void;
  fitToMovieListsExtent: (movieLists: GIMovieList[]) => void;
  moveCenterTo: (location: Location) => void;
}

// ExtremeMapの視点操作（初期表示範囲へのフィット・動画リスト範囲へのフィット・中心移動）
export function useExtremeMapViewControl(deps: Deps): ExtremeMapViewControlContext {
  const { olMap } = deps;

  // 初期表示範囲へのフィット。display:none の状態で実行すると getComputedStyle が 0 を返し、
  // 極小ビューポート基準で過剰にズームアウトした縮尺になるため、地図が表示・サイズ確定した後に呼ぶ。
  const applyInitialExtent = () => {
    const userSettings = deps.getUserSettings();
    if (!userSettings.initialExtent) return;

    const coords = userSettings.initialExtent.map((e: Location) => {
      return olMap.convCoord(e);
    });
    const options: FitOptions = {
      maxZoom: 20,
      duration: 500,
    };
    // ol6.0以上ではfit関数内に画面サイズが取れないため、パラメータで渡す
    const elem = document.getElementById(deps.olMapId.value);
    if (elem) {
      const metrics = window.getComputedStyle(elem);
      const width = parseInt(metrics.width, 10);
      const height = parseInt(metrics.height, 10);
      if (width > 0 && height > 0) {
        options.size = [width, height];
      }
    }
    olMap.fitToBoundingExtent(coords, options);
  };
  const fitToMovieListsExtent = (movieLists: GIMovieList[]) => {
    const coords: Coordinate[] = [];
    movieLists.forEach(movieList => {
      movieList.movies.forEach((m) => {
        m.movieGeoIndices.forEach((mgi: GIMovieGeoIndex) => {
          const coord = olMap.convCoord({
            lat: parseFloat(mgi.location.lat),
            lon: parseFloat(mgi.location.lon),
          });
          coords.push(coord);
        });
      });
    });
    olMap.fitToBoundingExtent(coords, {
      maxZoom: 16,
      duration: 500,
    });
  };
  // 中心点を移動して特定のズームに設定する.
  // (animationしようかとも思ったが、連打すると目が回るのでやめた.)
  const moveCenterTo = (location: Location) => {
    const pos = olMap.convCoord({
      lat: location.lat,
      lon: location.lon,
    });
    olMap.setCenter(pos);
  };

  return {
    applyInitialExtent,
    fitToMovieListsExtent,
    moveCenterTo,
  };
}
